'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ArrowUpRight } from 'lucide-react';
import { VideoHero } from '@/components/sections/hero/video-hero';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { Button } from '@/components/ui/button';
import { trackCtaClick } from '@/lib/analytics/track-cta-click';
import { cn } from '@/lib/utils';
import type { SanityLogoSet } from '@/sanity/lib/navigation';
import type { HeroSectionContent } from '@/sanity/lib/sections';

/** Static data uses `muxPlaybackId`; Sanity + GROQ can resolve `mux.video` → `asset.playbackId`. */
function muxPlaybackIdFromHeroVideo(video: {
  muxPlaybackId?: string | null;
  muxVideo?: { asset?: { playbackId?: string | null } | null } | null;
}): string | undefined {
  const manual = video.muxPlaybackId?.trim();
  if (manual) return manual;
  const id = video.muxVideo?.asset?.playbackId?.trim();
  return id || undefined;
}

function heroLogoHasRenderableAsset(logos: SanityLogoSet | null | undefined): boolean {
  if (!logos) return false;
  const theme = logos.enableThemeBasedLogoAlteration;
  const light = logos.lightLogo?.src;
  const dark = logos.darkLogo?.src;
  const base = logos.logo?.src;
  if (theme && (light || dark)) return true;
  return Boolean(base);
}

function HeroLogo({ logos }: { logos?: SanityLogoSet | null }) {
  const useThemeLogos = logos?.enableThemeBasedLogoAlteration;
  const baseLogo = logos?.logo?.src ? logos.logo : null;
  const lightLogo = logos?.lightLogo?.src ? logos.lightLogo : null;
  const darkLogo = logos?.darkLogo?.src ? logos.darkLogo : null;
  const className = 'h-12 sm:h-15 min-[1981px]:h-30 w-auto object-contain';
  const imageSize = { width: 780, height: 166 };

  let content: React.ReactNode = null;

  if (useThemeLogos && (lightLogo || darkLogo)) {
    content = (
      <>
        {lightLogo ? (
          <Image
            src={lightLogo.src as string}
            alt={lightLogo.alt || ''}
            width={imageSize.width}
            height={imageSize.height}
            priority
            className={cn(className, darkLogo && 'dark:hidden')}
          />
        ) : null}
        {darkLogo ? (
          <Image
            src={darkLogo.src as string}
            alt={darkLogo.alt || ''}
            width={imageSize.width}
            height={imageSize.height}
            priority
            className={cn(className, lightLogo && 'hidden dark:block')}
          />
        ) : null}
      </>
    );
  } else if (baseLogo?.src) {
    content = (
      <Image
        src={baseLogo.src}
        alt={baseLogo.alt || ''}
        width={imageSize.width}
        height={imageSize.height}
        priority
        className={className}
      />
    );
  }

  if (!content) return null;

  return (
    <div className="inline-flex rounded-xl bg-black/5 px-4 py-2 backdrop-blur-[2px] sm:mx-auto">
      {content}
    </div>
  );
}

export default function Hero({ content }: { content?: HeroSectionContent | null }) {
  if (!content) return null;

  const { background, header, ctaButtons, scrollHint, logos } = content;
  const muxPlaybackId =
    background?.type === 'video' ? muxPlaybackIdFromHeroVideo(background.video ?? {}) : undefined;
  const isVideoBg = Boolean(muxPlaybackId);
  const primaryCta = content.showCTA === false ? null : ctaButtons?.primary;
  const secondaryCta =
    ctaButtons?.showSecondary === false || content.showCTA === false ? null : ctaButtons?.secondary;
  const showScrollHint =
    content.showScrollHint !== false && Boolean(scrollHint?.label && scrollHint.href);

  const showHeroLogo =
    content.showLogoAboveHeader !== false && heroLogoHasRenderableAsset(logos ?? undefined);

  return (
    <VideoHero
      variant={isVideoBg ? 'video' : 'image'}
      muxPlaybackId={muxPlaybackId}
      posterSrc={!isVideoBg ? background?.image?.src || undefined : undefined}
      posterAlt={!isVideoBg ? background?.image?.alt || undefined : undefined}
    >
      <div
        className="relative z-10 mx-auto w-full max-w-7xl text-left sm:text-center space-y-6 sm:space-y-8 px-4 sm:px-6 min-[1324px]:px-0"
        id="hero"
      >
        {showHeroLogo ? <HeroLogo logos={logos} /> : null}
        {header?.tagline ? (
          <p className="mx-auto max-w-2xl text-pretty text-[10px] font-medium uppercase tracking-[0.2em] text-white/70 sm:text-xs">
            {header.tagline}
          </p>
        ) : null}
        <h1 className="mx-auto w-full max-w-3xl! text-3xl sm:text-5xl leading-tight sm:leading-tight text-white drop-shadow-2xl font-light">
          <SanityInlineText
            blocks={header?.heading}
            emphasisClassName="italic font-normal text-[#FCFAD7] dark:text-[#FCFAD7]"
          />
        </h1>
        {header?.subheading ? (
          <p className="mx-auto max-w-2xl text-pretty text-sm leading-relaxed tracking-wider text-white/90 sm:text-base font-light">
            {header.subheading}
          </p>
        ) : null}
        {primaryCta ? (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 xl:gap-6">
            <Link
              href={primaryCta.href}
              className="max-sm:w-full"
              data-lead-form-trigger={primaryCta.isLeadForm ? 'true' : undefined}
              data-tracking-location="hero_primary"
              onClick={() =>
                trackCtaClick({
                  location: 'hero_primary',
                  label: primaryCta.label,
                  href: primaryCta.href,
                  isLeadForm: primaryCta.isLeadForm,
                  variant: 'primary',
                })
              }
            >
              <Button
                variant="outline"
                size="lg"
                className="group cursor-pointer relative inline-flex border-none items-center gap-1.5 overflow-hidden rounded-full bg-[#62594F] hover:bg-[#6F665B] text-white px-4 h-10 text-xs font-bold transition-all duration-300 ease-in-out focus-visible:outline-none uppercase tracking-wider hover:text-white max-sm:w-full"
              >
                <span>{primaryCta.label}</span>
                <ArrowUpRight className="size-4 shrink-0 transition-all duration-300 ease-in-out group-hover:rotate-45" />
              </Button>
            </Link>
            {secondaryCta ? (
              <Link
                href={secondaryCta.href}
                className="max-sm:w-full"
                data-lead-form-trigger={secondaryCta.isLeadForm ? 'true' : undefined}
                data-tracking-location="hero_secondary"
                onClick={() =>
                  trackCtaClick({
                    location: 'hero_secondary',
                    label: secondaryCta.label,
                    href: secondaryCta.href,
                    isLeadForm: secondaryCta.isLeadForm,
                    variant: 'secondary',
                  })
                }
              >
                <Button
                  variant="ghost"
                  size="lg"
                  className="cursor-pointer relative inline-flex items-center gap-1.5 overflow-hidden rounded-full bg-white/5 border border-white/20 px-4 h-10 text-xs font-bold text-white backdrop-blur-xl transition-all duration-300 ease-in-out focus-visible:outline-none hover:border-[#BFB7A1] hover:bg-[#BFB7A1] hover:text-white uppercase tracking-wider max-sm:w-full"
                >
                  {secondaryCta.label}
                </Button>
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>
      {showScrollHint ? (
        <Link
          href={scrollHint!.href!}
          onClick={() =>
            trackCtaClick({
              location: 'hero_scroll_hint',
              label: scrollHint!.label ?? 'scroll',
              href: scrollHint!.href!,
            })
          }
        >
          <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-white/60 sm:bottom-8">
            <span className="text-[8px] font-medium uppercase tracking-[0.24em] text-white/50">
              {scrollHint!.label}
            </span>
            <div className="relative h-7 w-5 rounded-full border border-white/25 bg-white/5 shadow-[0_0_18px_rgba(255,255,255,0.08)] backdrop-blur-md">
              <div className="absolute left-1/2 top-1.5 h-1.5 w-0.5 -translate-x-1/2 rounded-full bg-white/70" />
            </div>
          </div>
        </Link>
      ) : null}
    </VideoHero>
  );
}
